import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import AppLayout from "@/components/layout/AppLayout";
import { useDemoState } from "@/data/demoState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  MessageSquare, Send, Clock, AlertTriangle, CheckCircle2,
  Phone, Image, Lock, Sparkles, Users, Bot, Zap, FileText,
  ChevronRight, ArrowUpRight, Activity, Target, RefreshCw
} from "lucide-react";

const statusConfig = {
  'awaiting-reply': { label: 'Awaiting', color: 'bg-amber-100 text-amber-700', dot: 'bg-amber-400' },
  'active': { label: 'Active', color: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-400' },
  'confirmed': { label: 'Confirmed', color: 'bg-blue-100 text-blue-700', dot: 'bg-blue-400' },
  'overdue': { label: 'Overdue', color: 'bg-red-100 text-red-700', dot: 'bg-red-400' },
};

const templates = [
  { id: 'tpl-1', name: 'Proof upload request', desc: 'Request site photos via upload link', body: 'Hi {name}, can you please upload photos of {condition} for {project}? Use this link: [upload link]. Clear daylight photos showing the full area. Thanks.' },
  { id: 'tpl-2', name: 'Consultant chase', desc: 'Follow up on overdue deliverable', body: 'Hi {name}, following up on {item} for {project}. This was due {date} and is currently blocking the next stage. Can you provide an update today?' },
  { id: 'tpl-3', name: 'Trade hold update', desc: 'Let a trade know they are on hold', body: 'Hi {name}, quick update on {project}. We have {count} conditions still to clear before we can release your trade. Current estimate is {days} days. Will keep you posted.' },
  { id: 'tpl-4', name: 'Certificate request', desc: 'Request a certificate from consultant', body: 'Hi {name}, we need the {certificate} for {project} ({condition}). Can you provide this by {date}? Upload link: [secure link].' },
  { id: 'tpl-5', name: 'SiteReady Trade Pass', desc: 'Send trade pass link for check-in', body: 'Hi {name}, your SiteReady Trade Pass for {project} is ready:\n{link}\n\nThis has your site notes, conditions, safety brief, and check-in. Tap to check in when you arrive.' },
  { id: 'tpl-6', name: 'Cannot Start follow-up', desc: 'Follow up on a Cannot Start report', body: 'Hi {name}, we received your Cannot Start report for {project}: "{reason}". Our supervisor is reviewing this now. We will update you once the issue is resolved.' },
];

function PhoneMockup({ thread, onSend }) {
  const [newMsg, setNewMsg] = useState('');
  return (
    <div className="bg-slate-900 rounded-[2rem] p-2 shadow-2xl max-w-[340px] mx-auto" data-testid="whatsapp-phone-mockup">
      <div className="bg-white rounded-[1.5rem] overflow-hidden">
        <div className="bg-[#075E54] px-4 py-3 flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white text-xs font-bold">
            {thread.recipient.split(' ').map(n => n[0]).join('')}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-medium truncate">{thread.recipient}</p>
            <p className="text-white/70 text-[10px]">{thread.recipientRole}</p>
          </div>
          <Phone className="w-4 h-4 text-white/60" />
        </div>
        <div className="bg-[#ECE5DD] px-3 py-3 min-h-[340px] max-h-[340px] overflow-y-auto space-y-2"
          style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'200\' height=\'200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cdefs%3E%3Cpattern id=\'p\' width=\'40\' height=\'40\' patternUnits=\'userSpaceOnUse\'%3E%3Cpath d=\'M0 20h40M20 0v40\' fill=\'none\' stroke=\'%23d5cec6\' stroke-width=\'.5\'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width=\'200\' height=\'200\' fill=\'%23ECE5DD\'/%3E%3Crect width=\'200\' height=\'200\' fill=\'url(%23p)\' opacity=\'.3\'/%3E%3C/svg%3E")' }}>
          {thread.messages.map(msg => (
            <div key={msg.id} className={`flex ${msg.direction === 'out' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-lg px-3 py-2 shadow-sm ${msg.direction === 'out' ? 'bg-[#DCF8C6]' : 'bg-white'}`}>
                {msg.type === 'media' ? (
                  <div className="flex items-center gap-2 text-xs text-slate-600">
                    <Image className="w-4 h-4 text-emerald-600" />
                    <span>{msg.text}</span>
                  </div>
                ) : (
                  <p className="text-[13px] text-slate-800 leading-relaxed whitespace-pre-wrap">{msg.text}</p>
                )}
                <p className="text-[9px] text-slate-400 text-right mt-1">{msg.time.split(' ')[1]}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="bg-[#F0F0F0] px-2 py-2 flex items-center gap-2">
          <input value={newMsg} onChange={e => setNewMsg(e.target.value)} placeholder="Type a message..."
            className="flex-1 bg-white rounded-full px-4 py-2 text-xs text-slate-700 placeholder:text-slate-400 focus:outline-none"
            data-testid="wa-message-input"
            onKeyDown={e => { if (e.key === 'Enter' && newMsg.trim()) { onSend(newMsg); setNewMsg(''); } }} />
          <button onClick={() => { if (newMsg.trim()) { onSend(newMsg); setNewMsg(''); } }}
            className="w-8 h-8 rounded-full bg-[#075E54] flex items-center justify-center" data-testid="wa-send-btn">
            <Send className="w-3.5 h-3.5 text-white" />
          </button>
        </div>
      </div>
    </div>
  );
}

function AIExtractionCard({ label, value, confidence, type }) {
  return (
    <div className="bg-white rounded-lg border border-slate-100 p-2.5 group hover:border-blue-200 transition-colors">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-400">{label}</span>
        {confidence && (
          <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${confidence > 85 ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
            {confidence}%
          </span>
        )}
      </div>
      <p className="text-xs font-medium text-slate-800">{value}</p>
      {type && <p className="text-[9px] text-slate-400 mt-0.5">{type}</p>}
    </div>
  );
}

export default function WhatsappActionRail() {
  const { whatsappThreads, blockers, logAction, sendChase } = useDemoState();
  const [activeThread, setActiveThread] = useState(whatsappThreads[0]?.id || null);
  const [activeTab, setActiveTab] = useState('active');
  const [aiProcessing, setAiProcessing] = useState(false);

  const thread = whatsappThreads.find(t => t.id === activeThread);
  const linkedBlocker = thread ? blockers.find(b => b.id === thread.linkedBlockerId) : null;

  const awaiting = whatsappThreads.filter(t => t.status === 'awaiting-reply');
  const overdue = whatsappThreads.filter(t => t.status === 'overdue');

  const aiExtractions = useMemo(() => {
    if (!thread) return [];
    const inbound = thread.messages.filter(m => m.direction === 'in');
    return inbound.map(m => ({
      id: m.id,
      text: m.text.slice(0, 60) + (m.text.length > 60 ? '...' : ''),
      type: m.type === 'media' ? 'Photo / Document' : 'Text reply',
      confidence: m.type === 'media' ? 92 : 78,
      intent: m.type === 'media' ? 'Evidence submission' : 'Status update',
      time: m.time,
    }));
  }, [thread]);

  const suggestedReplies = useMemo(() => {
    if (!linkedBlocker) return ['Thanks, received. Will review shortly.', 'Can you also send the certificate?'];
    return [
      `Thanks. Reviewing now. Will update ${linkedBlocker.title} status.`,
      `Received. Can you confirm this covers ${linkedBlocker.title}?`,
      `Got it. Still need the certificate for ${linkedBlocker.stage}. Can you send today?`,
    ];
  }, [linkedBlocker]);

  const handleSend = (text) => {
    logAction(`WhatsApp sent to ${thread?.recipient}: "${text.slice(0, 40)}..."`, 'whatsapp');
    toast.success('Message sent', { description: `To ${thread?.recipient}` });
  };

  const handleRunExtraction = () => {
    setAiProcessing(true);
    setTimeout(() => {
      setAiProcessing(false);
      toast.success('AI extraction complete', { description: `${aiExtractions.length} items extracted from ${thread?.recipient}` });
    }, 1500);
  };

  const handleChase = () => {
    if (linkedBlocker) {
      sendChase(linkedBlocker.id, 'whatsapp');
      toast.success('Chase sent via WhatsApp', { description: linkedBlocker.owner });
    }
  };

  return (
    <AppLayout>
      <div className="flex gap-0 -mx-6 lg:-mx-8 -mt-6" style={{ height: 'calc(100vh - 88px)' }} data-testid="whatsapp-rail-page">
        {/* Left: Thread list */}
        <div className="w-[280px] shrink-0 border-r border-slate-200 bg-white overflow-y-auto" data-testid="wa-thread-list">
          <div className="p-3 border-b border-slate-100">
            <div className="flex items-center gap-2 mb-3">
              <MessageSquare className="w-4 h-4 text-[#075E54]" />
              <span className="text-sm font-semibold text-slate-900">Threads</span>
              <Badge className="bg-slate-100 text-slate-600 text-[9px] ml-auto">{whatsappThreads.length}</Badge>
            </div>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="w-full bg-slate-100 h-8">
                <TabsTrigger value="active" className="text-[10px] flex-1 h-6">All</TabsTrigger>
                <TabsTrigger value="awaiting" className="text-[10px] flex-1 h-6">
                  Awaiting {awaiting.length > 0 && <span className="ml-1 bg-amber-500 text-white rounded-full w-3.5 h-3.5 text-[8px] inline-flex items-center justify-center">{awaiting.length}</span>}
                </TabsTrigger>
                <TabsTrigger value="templates" className="text-[10px] flex-1 h-6">Templates</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {activeTab === 'templates' ? (
            <div className="p-3 space-y-2">
              {templates.map(t => (
                <div key={t.id} className="p-3 bg-slate-50 rounded-lg border border-slate-100 hover:border-slate-200 transition-colors cursor-pointer" data-testid={`wa-template-${t.id}`}>
                  <p className="text-xs font-semibold text-slate-700">{t.name}</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">{t.desc}</p>
                  <p className="text-[10px] text-slate-500 mt-2 bg-white rounded p-2 border border-slate-100 line-clamp-2">{t.body}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {(activeTab === 'awaiting' ? awaiting : whatsappThreads).map(t => {
                const sc = statusConfig[t.status] || statusConfig.active;
                const hasUnread = t.messages.some(m => m.direction === 'in');
                return (
                  <button key={t.id} onClick={() => { setActiveThread(t.id); if (activeTab === 'awaiting') setActiveTab('active'); }}
                    data-testid={`wa-thread-${t.id}`}
                    className={`w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-slate-50 transition-colors ${t.id === activeThread ? 'bg-blue-50/70 border-l-2 border-l-blue-500' : 'border-l-2 border-l-transparent'}`}>
                    <div className="relative">
                      <div className="w-9 h-9 rounded-full bg-[#075E54] flex items-center justify-center text-white text-xs font-bold shrink-0">
                        {t.recipient.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${sc.dot}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className={`text-xs font-semibold truncate ${hasUnread ? 'text-slate-900' : 'text-slate-600'}`}>{t.recipient}</p>
                      </div>
                      <p className="text-[10px] text-slate-400 truncate">{t.recipientRole} · {t.project}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Badge className={`text-[8px] ${sc.color}`}>{sc.label}</Badge>
                      <span className="text-[9px] text-slate-300">{t.lastSent}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Center: Phone mockup */}
        <div className="flex-1 flex flex-col min-w-0" data-testid="wa-phone-area">
          {/* Suggested replies bar */}
          {thread && (
            <div className="bg-white border-b border-slate-100 px-4 py-2 flex items-center gap-2 overflow-x-auto">
              <Sparkles className="w-3.5 h-3.5 text-blue-500 shrink-0" />
              <span className="text-[10px] text-slate-400 shrink-0">Quick replies:</span>
              {suggestedReplies.map((r, i) => (
                <button key={i} onClick={() => handleSend(r)}
                  className="shrink-0 text-[10px] bg-blue-50 text-blue-700 border border-blue-100 rounded-full px-3 py-1 hover:bg-blue-100 transition-colors"
                  data-testid={`wa-suggested-reply-${i}`}>
                  {r.slice(0, 50)}{r.length > 50 ? '...' : ''}
                </button>
              ))}
            </div>
          )}
          <div className="flex-1 flex items-center justify-center bg-slate-50/80 py-6">
            {thread ? (
              <PhoneMockup thread={thread} onSend={handleSend} />
            ) : (
              <div className="text-center">
                <MessageSquare className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-400">Select a thread to view</p>
              </div>
            )}
          </div>
        </div>

        {/* Right: AI Agent Activity Panel */}
        <div className="w-[300px] shrink-0 border-l border-slate-200 bg-white overflow-y-auto" data-testid="wa-ai-panel">
          <div className="p-4 space-y-4">
            {thread && (
              <>
                {/* AI Agent Header */}
                <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl p-3 text-white">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-7 h-7 bg-blue-500/20 rounded-lg flex items-center justify-center">
                      <Bot className="w-4 h-4 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold">AI Agent Activity</p>
                      <p className="text-[9px] text-slate-400">Monitoring this thread</p>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-2">
                    <Button size="sm" variant="ghost" className="h-7 text-[10px] text-slate-300 hover:text-white hover:bg-white/10 gap-1"
                      onClick={handleRunExtraction} disabled={aiProcessing} data-testid="wa-run-extraction-btn">
                      {aiProcessing ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
                      {aiProcessing ? 'Processing...' : 'Run extraction'}
                    </Button>
                    {linkedBlocker && (
                      <Button size="sm" variant="ghost" className="h-7 text-[10px] text-slate-300 hover:text-white hover:bg-white/10 gap-1"
                        onClick={handleChase} data-testid="wa-chase-btn">
                        <Send className="w-3 h-3" />Chase
                      </Button>
                    )}
                  </div>
                </div>

                {/* Thread Context */}
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Thread context</p>
                  <div className="grid grid-cols-2 gap-1.5">
                    <div className="bg-slate-50 rounded-lg p-2"><p className="text-[9px] text-slate-400">Project</p><p className="text-[11px] font-medium text-slate-700 truncate">{thread.project}</p></div>
                    <div className="bg-slate-50 rounded-lg p-2"><p className="text-[9px] text-slate-400">Last sent</p><p className="text-[11px] font-medium text-slate-700">{thread.lastSent}</p></div>
                    <div className="col-span-2 bg-slate-50 rounded-lg p-2"><p className="text-[9px] text-slate-400">Recipient</p><p className="text-[11px] font-medium text-slate-700">{thread.recipient} · {thread.recipientRole}</p></div>
                  </div>
                </div>

                {/* Linked Blocker */}
                {linkedBlocker && (
                  <>
                    <Separator />
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1">
                        <Target className="w-3 h-3" /> Linked blocker
                      </p>
                      <div className="bg-red-50 border border-red-100 rounded-lg p-3">
                        <div className="flex items-center gap-1.5 mb-1">
                          <AlertTriangle className="w-3 h-3 text-red-500" />
                          <Badge className="text-[9px] bg-red-100 text-red-700">{linkedBlocker.severity}</Badge>
                          {linkedBlocker.chaseCount > 0 && (
                            <Badge className="text-[9px] bg-slate-100 text-slate-600">Chased {linkedBlocker.chaseCount}x</Badge>
                          )}
                        </div>
                        <p className="text-xs font-medium text-slate-800">{linkedBlocker.title}</p>
                        <p className="text-[10px] text-slate-500 mt-0.5">{linkedBlocker.stage} · Due {linkedBlocker.dueDate}</p>
                        {linkedBlocker.readinessImpact && (
                          <p className="text-[10px] text-red-600 mt-1.5 bg-red-100/50 rounded px-2 py-1">{linkedBlocker.readinessImpact}</p>
                        )}
                      </div>
                    </div>
                  </>
                )}

                {/* AI Extracted Data */}
                <Separator />
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                      <Activity className="w-3 h-3" /> AI extracted data
                    </p>
                    <Badge className="text-[8px] bg-blue-50 text-blue-600">{aiExtractions.length} items</Badge>
                  </div>
                  {aiExtractions.length > 0 ? (
                    <div className="space-y-1.5">
                      {aiExtractions.map(ex => (
                        <AIExtractionCard key={ex.id} label={ex.intent} value={ex.text} confidence={ex.confidence} type={ex.type} />
                      ))}
                    </div>
                  ) : (
                    <div className="bg-slate-50 rounded-lg p-3 text-center">
                      <Bot className="w-5 h-5 text-slate-300 mx-auto mb-1" />
                      <p className="text-[10px] text-slate-400">No inbound messages to extract</p>
                    </div>
                  )}
                </div>

                {/* What this means */}
                <Separator />
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Why WhatsApp</p>
                  <div className="bg-blue-50 rounded-lg p-2.5 space-y-1.5">
                    <p className="text-[10px] text-blue-700 leading-relaxed">External recipients reply directly. No login needed. AI extracts replies back into easypermit and links to the relevant blocker, stage, and readiness state.</p>
                    <div className="flex items-center gap-1 text-[9px] text-blue-600">
                      <CheckCircle2 className="w-3 h-3" />
                      <span>Replies auto-linked to blockers and conditions</span>
                    </div>
                    <div className="flex items-center gap-1 text-[9px] text-blue-600">
                      <CheckCircle2 className="w-3 h-3" />
                      <span>Media uploads trigger proof review pipeline</span>
                    </div>
                  </div>
                </div>

                {/* Navigation */}
                <div className="flex flex-wrap gap-1.5">
                  <Link to="/builder/job-blocker" className="flex items-center gap-1 px-2.5 py-1.5 border border-slate-200 rounded-lg text-[10px] font-medium text-slate-500 hover:text-blue-600 hover:border-blue-200 transition-all">
                    <AlertTriangle className="w-3 h-3" />Job Blocker
                  </Link>
                  <Link to="/builder/stage-readiness" className="flex items-center gap-1 px-2.5 py-1.5 border border-slate-200 rounded-lg text-[10px] font-medium text-slate-500 hover:text-blue-600 hover:border-blue-200 transition-all">
                    <Lock className="w-3 h-3" />Readiness
                  </Link>
                  <Link to="/builder/proof-review" className="flex items-center gap-1 px-2.5 py-1.5 border border-slate-200 rounded-lg text-[10px] font-medium text-slate-500 hover:text-blue-600 hover:border-blue-200 transition-all">
                    <FileText className="w-3 h-3" />Proof
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
